import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Button, Tabs, Tab, Input, Spinner } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGetAiWorkerUsageByIdMutation } from '@circuitry-ai/doc-data';
import { CtyShowToast, checkType } from '@circuitry-ai/doc-ui';
import { ToolTranscriptCard } from './ToolTranscriptCard';

export interface CallLogDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  callLogId: string | null;
}

interface LogEntry {
  timestamp: string;
  type: string;
  service: string;
  metric: string;
  value: string;
  runtime: string;
}

export interface CallDetail {
  type: string;
  recordingUrl?: string;
  sessionLog?: string;
  messages?: Array<{
    role: string;
    message?: string;
    text?: string;
    type?: string;
    timestamp?: string;
    secondsFromStart?: number;
    tool_details?: Record<string, unknown>;
  }>;
  costs?: Array<{
    type: string;
    analysis_type?: string;
    cost: number;
    model?: { model?: string; provider?: string };
    transcriber?: { model?: string; provider?: string };
    voice?: { model?: string; provider?: string };
    minutes?: number;
    characters?: number;
    prompt_tokens?: number;
    completion_tokens?: number;
  }>;
  summary?: {
    summary: string;
    outcome: string;
    reasoning: string;
    model: string;
    usage?: {
      prompt_tokens?: number;
      completion_tokens?: number;
      total_tokens?: number;
    };
  };
}

export type TranscriptMessage = NonNullable<CallDetail['messages']>[number];
const displayTimestamp = (timestamp: string | undefined): string => timestamp || 'N/A';

const parseSessionLog = (logContent: string): LogEntry[] => {
  const lines = logContent.split('\n');
  return lines
    .map((line) => {
      // Parse without backtracking-vulnerable regex: "timestamp | type[opt]: content"
      const pipeIdx = line.indexOf(' | ');
      if (pipeIdx < 0) return null;
      const timestamp = line.slice(0, pipeIdx).trim();
      const rest = line.slice(pipeIdx + 3);
      const colonIdx = rest.indexOf(': ');
      if (colonIdx < 0) return null;
      const typePart = rest.slice(0, colonIdx).trim();
      const content = rest.slice(colonIdx + 2);
      const bracketStart = typePart.indexOf('[');
      const type = bracketStart >= 0 ? typePart.slice(0, bracketStart).trim() : typePart;

      if (type.startsWith('PERF')) {
        const atIdx = content.indexOf('(at ');
        const closeIdx = content.indexOf(')', atIdx);
        if (atIdx >= 0 && closeIdx > atIdx) {
          const metric = content.slice(0, atIdx).trim();
          const runtime = content.slice(atIdx + 4, closeIdx);
          const numStr = metric.split(' ')[0];
          const numVal = numStr ? Number.parseFloat(numStr) : Number.NaN;
          const value = Number.isNaN(numVal) ? '0s' : `${numVal}s`;
          return {
            timestamp,
            type: 'PERF',
            service: '',
            metric: numStr ?? '',
            value,
            runtime,
          };
        }
      } else if (type.startsWith('ASSISTANT')) {
        return {
          timestamp,
          type: 'ASSISTANT',
          service: 'Assistant',
          metric: 'Message',
          value: content.replace('(pre-canned): ', ''),
          runtime: '',
        };
      } else if (type.startsWith('STT')) {
        const value = content.startsWith('"') && content.endsWith('"')
          ? content.slice(1, -1)
          : content;
        return {
          timestamp,
          type: 'STT',
          service: 'Speech-to-Text',
          metric: 'Transcription',
          value,
          runtime: '',
        };
      } else if (type.startsWith('USAGE')) {
        const colonPos = content.indexOf(':');
        if (colonPos >= 0) {
          const metric = content.slice(0, colonPos).trim();
          const value = content.slice(colonPos + 1).trim();
          return {
            timestamp,
            type: 'USAGE',
            service: '',
            metric,
            value,
            runtime: '',
          };
        }
      } else if (type.startsWith('EVENT')) {
        return {
          timestamp,
          type: 'EVENT',
          service: 'System',
          metric: 'Event',
          value: content,
          runtime: '',
        };
      } else if (type.startsWith('LATENCY')) {
        return {
          timestamp,
          type: 'LATENCY',
          service: 'System',
          metric: 'Latency',
          value: content,
          runtime: '',
        };
      }
      return null;
    })
    .filter((entry): entry is LogEntry => entry !== null);
};








export const CallLogDrawer: React.FC<Readonly<CallLogDrawerProps>> = ({
  isOpen,
  onClose,
  callLogId,
}) => {
  const { t } = useTranslation();
  const [callDetail, setCallDetail] = useState<CallDetail | null>(null);
  const [activeTab, setActiveTab] = useState('transcripts');
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackTime, setPlaybackTime] = useState('0:00');
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [logEntries, setLogEntries] = useState<LogEntry[]>([]);
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedMessages, setExpandedMessages] = useState<Record<number, boolean>>({});
  const [copied, setCopied] = useState(false);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);
  const [rawData, setRawData] = useState<Record<string, unknown> | null>(null);

  const [fetchUsageById, { isLoading }] = useGetAiWorkerUsageByIdMutation();

  useEffect(() => {
    if (isOpen && callLogId) {
      fetchUsageById(callLogId)
        .unwrap()
        .then((res: { data?: Record<string, unknown> } & Record<string, unknown>) => {
          const data = res?.data ?? res;
          setRawData(data as Record<string, unknown>);
        })
        .catch(() => setRawData(null));
    } else {
      setRawData(null);
      setCallDetail(null);
      setLogEntries([]);
    }
  }, [isOpen, callLogId, fetchUsageById]);

  useEffect(() => {
    if (!rawData) return;
    const content = (rawData as { content?: Array<Record<string, unknown>> }).content ?? [];
    const audioItem = content.find((c) => c.artifact_type === 'audio');
    const transcriptItem = content.find((c) => c.artifact_type === 'transcript');
    const metricsItem = content.find((c) => c.artifact_type === 'metrics');
    const summaryItem = content.find((c) => c.artifact_type === 'summary');
    const sessionLogItem = content.find((c) => c.artifact_type === 'session_log');

    if (sessionLogItem?.s3_location) {
      fetch(sessionLogItem.s3_location as string)
        .then((r) => (r.ok ? r.text() : ''))
        .then((text) => setLogEntries(parseSessionLog(text)));
    }

    const summaryContent = summaryItem?.content as Record<string, unknown> | undefined;
    const summaryData = summaryContent?.summary;
    const summaryText =
      typeof summaryData === 'string'
        ? summaryData
        : summaryData && typeof summaryData === 'object' && Array.isArray((summaryData as { conversation?: unknown[] }).conversation)
          ? (summaryData as { conversation: Array<Record<string, string>> })
            .conversation.map((turn) => {
              const key = Object.keys(turn).find(
                (k) => k === 'assistant' || k === 'user' || k === 'caller' || k === 'recipient'
              );
              if (!key) return '';
              const speaker = key === 'assistant' || key === 'caller' ? 'Assistant' : 'Customer';
              return `${speaker}: ${turn[key]}`;
            })
            .join('\n\n')
          : JSON.stringify(summaryData ?? 'No summary', null, 2);

    setCallDetail({
      type: (rawData as { agent_type?: string }).agent_type ?? '',
      recordingUrl: audioItem?.s3_location as string | undefined,
      sessionLog: sessionLogItem?.s3_location as string | undefined,
      messages: (transcriptItem?.content as { messages?: Array<Record<string, unknown>> })?.messages?.map(
        (m) => ({
          role: m.role as string,
          message: (m.text ?? m.message) as string,
          text: (m.text ?? m.message) as string,
          type: m.type as string,
          timestamp: m.timestamp as string,
          secondsFromStart: (m.seconds_from_start ?? m.secondsFromStart) as number,
          tool_details: m.tool_details as Record<string, unknown>,
        })
      ),
      costs: metricsItem?.content
        ? [
          {
            type: 'analysis',
            cost: 0,
            prompt_tokens: (metricsItem.content as { token_usage?: { prompt_tokens?: number } })
              .token_usage?.prompt_tokens,
            completion_tokens: (metricsItem.content as { token_usage?: { completion_tokens?: number } })
              .token_usage?.completion_tokens,
          },
        ]
        : [],
      summary: summaryContent
        ? {
          summary: summaryText,
          outcome: (summaryContent.outcome as string) ?? 'N/A',
          reasoning: (summaryContent.reasoning as string) ?? 'N/A',
          model: (summaryContent.model as string) ?? 'Unknown',
          usage: summaryContent.usage as { prompt_tokens?: number; completion_tokens?: number; total_tokens?: number },
        }
        : undefined,
    });
  }, [rawData]);

  useEffect(() => {
    if (callDetail?.recordingUrl) {
      const audio = new Audio(callDetail.recordingUrl);
      const onLoadedMetadata = () => setDuration(audio.duration);
      const onTimeUpdate = () => {
        setCurrentTime(audio.currentTime);
        const minutes = Math.floor(audio.currentTime / 60);
        const seconds = Math.floor(audio.currentTime % 60);
        setPlaybackTime(`${minutes}:${seconds.toString().padStart(2, '0')}`);
      };
      const onEnded = () => setIsPlaying(false);
      audio.addEventListener('loadedmetadata', onLoadedMetadata);
      audio.addEventListener('timeupdate', onTimeUpdate);
      audio.addEventListener('ended', onEnded);
      setAudioElement(audio);
      return () => {
        audio.pause();
        audio.removeEventListener('loadedmetadata', onLoadedMetadata);
        audio.removeEventListener('timeupdate', onTimeUpdate);
        audio.removeEventListener('ended', onEnded);
        setAudioElement(null);
      };
    }
  }, [callDetail?.recordingUrl]);

  const handleClose = useCallback(() => {
    if (audioElement) {
      audioElement.pause();
      audioElement.currentTime = 0;
    }
    setIsPlaying(false);
    onClose();
  }, [onClose, audioElement]);

  const togglePlayback = () => {
    if (!audioElement) return;
    if (isPlaying) {
      audioElement.pause();
    } else {
      void audioElement.play().catch(() => undefined);
    }
    setIsPlaying(!isPlaying);
  };

  const seekBackward = () => {
    if (audioElement) {
      audioElement.currentTime = Math.max(0, audioElement.currentTime - 10);
    }
  };

  const seekForward = () => {
    if (audioElement) {
      audioElement.currentTime = Math.min(audioElement.duration, audioElement.currentTime + 10);
    }
  };

  const handleWaveformClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioElement || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const newTime = (clickX / rect.width) * duration;
    audioElement.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const downloadAudio = async () => {
    const url = callDetail?.recordingUrl;
    if (url && callLogId) {
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error('Failed to fetch');
        const blob = await response.blob();
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = `call-recording-${callLogId}.mp3`;
        link.click();
        window.URL.revokeObjectURL(link.href);
        CtyShowToast(checkType.success, t('Download started.'));
      } catch {
        CtyShowToast(checkType.error, t('Failed to download.'));
      }
    }
  };

  const embedUrl = callDetail?.recordingUrl ?? '';
  const iframeCode = embedUrl
    ? `<iframe src="${embedUrl}" width="100%" height="200" title="Call Recording" class="rounded-md"></iframe>`
    : '';

  const copyEmbedCode = async () => {
    if (!iframeCode) {
      CtyShowToast(checkType.error, t('No recording URL.'));
      return;
    }
    try {
      await navigator.clipboard.writeText(iframeCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      CtyShowToast(checkType.success, t('Embed code copied.'));
    } catch {
      CtyShowToast(checkType.error, t('Copy failed.'));
    }
  };

  const toggleMessageDetails = (index: number) => {
    setExpandedMessages((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const filteredLogs = logEntries.filter((entry) => {
    const matchesType = filterType === 'all' || entry.type === filterType;
    const matchesSearch =
      searchTerm === '' ||
      [entry.timestamp, entry.service, entry.metric, entry.value, entry.runtime].some(
        (v) => v.toLowerCase().includes(searchTerm.toLowerCase())
      );
    return matchesType && matchesSearch;
  });

  const isToolTranscriptMessage = (message: TranscriptMessage): boolean =>
    (message.role === 'assistant' || message.role === 'bot') &&
    (message.type === 'tool_call' || message.type === 'tool_result');


  const toolTranscriptEntries = useMemo(() => {
    if (!callDetail?.messages?.length) return [];
    return callDetail.messages
      .map((message, index) => ({ message, index }))
      .filter(({ message }) => isToolTranscriptMessage(message));
  }, [callDetail?.messages]);

  const getLogTypeColor = (type: string) => {
    switch (type) {
      case 'PERF':
        return 'bg-primary/20 text-primary';
      case 'ASSISTANT':
        return 'bg-danger/20 text-danger';
      case 'STT':
        return 'bg-secondary/20 text-secondary';
      case 'USAGE':
        return 'bg-success/20 text-success';
      case 'EVENT':
        return 'bg-warning/20 text-warning';
      case 'LATENCY':
        return 'bg-warning/20 text-warning';
      default:
        return 'bg-default-200 text-default-700';
    }
  };

  if (!callLogId) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50"
            onClick={handleClose}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClose();
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={t('Close')}
          />
          <motion.div
            key="drawer"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 z-50 h-full w-full max-w-2xl 2xl:max-w-[1000px] border-l border-divider bg-background overflow-y-auto shadow-xl"
          >
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-divider bg-background p-4">
              <div className="flex items-center gap-2">
                <Icon icon="mdi:phone" className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold">{t('Call Log Details')}</h2>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-default-500 font-mono">{callLogId}</span>
                <Button isIconOnly size="sm" variant="light" onPress={handleClose} aria-label={t('Close')}>
                  <Icon icon="mdi:close" className="w-5 h-5" />
                </Button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Recording section */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary rounded-lg">
                      <Icon icon="mdi:volume-high" className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-xl font-bold">{t('Recording')}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      size="sm"
                      variant="flat"
                      onPress={copyEmbedCode}
                      isDisabled={!embedUrl}
                      startContent={
                        copied ? <Icon icon="mdi:check" className="w-4 h-4" /> : <Icon icon="mdi:link" className="w-4 h-4" />
                      }
                    >
                      {copied ? t('Copied') : t('Copy link')}
                    </Button>
                    <div className="text-right">
                      <div className="text-lg font-mono font-bold">{playbackTime}</div>
                      <div className="text-sm text-default-500">
                        / {Math.floor(duration / 60)}:{Math.floor(duration % 60).toString().padStart(2, '0')}
                      </div>
                    </div>
                  </div>
                </div>

                {callDetail?.recordingUrl ? (
                  <div className="bg-gradient-to-br from-primary/10 to-primary/20 rounded-2xl p-6 mb-6 border border-primary/30 shadow-lg">
                    <div
                      className="relative h-24 bg-background/90 rounded-xl overflow-hidden border border-primary/30 backdrop-blur-md cursor-pointer hover:shadow-inner transition-shadow"
                      onClick={handleWaveformClick}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          if (audioElement && duration) {
                            const newTime = duration / 2;
                            audioElement.currentTime = newTime;
                            setCurrentTime(newTime);
                          }
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-label={t('Seek playback position')}
                    >
                      <div className="flex items-center justify-center h-full px-4 py-2">
                        <div className="flex items-end gap-[2px] w-full max-w-2xl relative">
                          {Array.from({ length: 120 }, (_, i) => {
                            const isActive = duration > 0 && i / 120 <= currentTime / duration;
                            return (
                              <div
                                key={i}
                                className="rounded-sm transition-all duration-200 ease-in-out"
                                style={{
                                  height: `${Math.sin(i * 0.1) * 30 + 40}px`,
                                  width: '3px',
                                  background: isActive
                                    ? 'rgb(17 71 130 / 0.8)'
                                    : 'rgb(17 71 130 / 0.2)',
                                  opacity: isActive ? 1 : 0.6,
                                  transform: isActive ? 'scaleY(1.05)' : 'scaleY(1)',
                                }}
                              />
                            );
                          })}
                          {duration > 0 && (
                            <div
                              className="absolute top-0 bottom-0 w-1 bg-primary opacity-95 rounded-full shadow-lg transition-all duration-150"
                              style={{ left: `${(currentTime / duration) * 100}%` }}
                            >
                              <div className="absolute -top-2 -left-2 w-4 h-4 bg-primary rounded-full shadow-md border border-primary/30" />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-center items-center gap-3 mt-4">
                      <Button
                        variant="flat"
                        size="sm"
                        className="border border-primary/30 text-primary hover:text-primary hover:bg-primary/50 hover:border-primary rounded-full min-w-10 h-10 p-0"
                        onPress={seekBackward}
                        isDisabled={!audioElement}
                        aria-label={t('Rewind')}
                      >
                        <Icon icon="mdi:rewind" className="w-5 h-5" />
                      </Button>
                      <Button
                        onPress={togglePlayback}
                        className="bg-primary/80 hover:bg-primary shadow-lg shadow-primary/30 text-white transition-all duration-200 rounded-full min-w-12 h-12 p-0"
                        isDisabled={!callDetail?.recordingUrl}
                        aria-label={isPlaying ? t('Pause') : t('Play')}
                      >
                        <Icon icon={isPlaying ? 'mdi:pause' : 'mdi:play'} className="w-6 h-6" />
                      </Button>
                      <Button
                        variant="flat"
                        size="sm"
                        className="border border-primary/30 text-primary hover:text-primary hover:bg-primary/50 hover:border-primary rounded-full min-w-10 h-10 p-0"
                        onPress={seekForward}
                        isDisabled={!audioElement}
                        aria-label={t('Fast forward')}
                      >
                        <Icon icon="mdi:fast-forward" className="w-5 h-5" />
                      </Button>
                      <Button
                        variant="flat"
                        size="sm"
                        className="border border-primary/30 text-primary hover:text-primary hover:bg-primary/50 hover:border-primary rounded-full px-4 py-2"
                        onPress={downloadAudio}
                        isDisabled={!callDetail?.recordingUrl}
                        startContent={<Icon icon="mdi:download" className="w-4 h-4" />}
                      >
                        {t('Download')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-xl border border-divider bg-default-50 p-6 text-center">
                    <div className="w-16 h-16 rounded-full bg-default-200 flex items-center justify-center mx-auto mb-4">
                      <Icon icon="mdi:volume-high" className="w-8 h-8 text-default-400" />
                    </div>
                    <p className="text-default-500">{t('No audio recording available for this call.')}</p>
                  </div>
                )}
              </div>

              {/* Tabs */}
              <Tabs
                selectedKey={activeTab}
                onSelectionChange={(k) => setActiveTab(k as string)}
                classNames={{ tabList: 'gap-2' }}
              >
                <Tab key="transcripts" title={t('Transcripts')}>
                  <div className="rounded-xl border border-divider bg-default-50 p-6">
                    {isLoading && (
                      <div className="flex items-center gap-3 p-4">
                        <Spinner size="sm" />
                        <span>{t('Loading transcript...')}</span>
                      </div>
                    )}
                    {callDetail?.messages && callDetail.messages.length > 0 ? (
                      <div className="max-h-[min(70vh,28rem)] space-y-4 overflow-y-auto overflow-x-hidden pr-1 min-w-0">
                        {callDetail.messages.map((message, index) => {
                          if (message.role === 'system' && message.type === 'error') {
                            return (
                              <div key={message.timestamp ?? index} className="flex justify-center">
                                <div
                                  className="rounded-xl p-4 bg-danger/10 border border-danger cursor-pointer"
                                  onClick={() => toggleMessageDetails(index)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      toggleMessageDetails(index);
                                    }
                                  }}
                                  role="button"
                                  tabIndex={0}
                                  aria-expanded={expandedMessages[index]}
                                  aria-label={expandedMessages[index] ? t('Hide error details') : t('View error details')}
                                >
                                  <div className="flex items-center gap-2">
                                    <Icon icon="mdi:alert-circle" className="w-4 h-4 text-danger" />
                                    {t('Error')} ({expandedMessages[index] ? t('hide') : t('view')})
                                    <Icon icon={expandedMessages[index] ? 'mdi:chevron-up' : 'mdi:chevron-down'} className="w-4 h-4" />
                                  </div>
                                  {expandedMessages[index] && message.text && (
                                    <p className="mt-2 text-sm">{message.text}</p>
                                  )}
                                </div>
                              </div>
                            );
                          }
                          if (isToolTranscriptMessage(message)) {
                            return (
                              <ToolTranscriptCard
                                key={`${message.timestamp ?? ''}-${message.type}-${index}`}
                                message={message}
                                index={index}
                                expanded={!!expandedMessages[index]}
                                onToggle={toggleMessageDetails}
                              />
                            );
                          }
                          if (message.role === 'assistant' || message.role === 'user' || message.role === 'bot') {
                            const isBot = message.role === 'bot' || message.role === 'assistant';
                            return (
                              <div key={index} className={`flex ${isBot ? 'justify-start' : 'justify-end'}`}>
                                <div className={`max-w-[80%] ${isBot ? 'mr-8' : 'ml-8'}`}>
                                  <div className={`flex items-center gap-2 mb-1 ${isBot ? 'justify-start' : 'justify-end'}`}>
                                    <span className="text-xs font-medium text-default-500">
                                      {isBot ? t('Assistant') : t('You')}
                                    </span>
                                    <span className="text-xs text-default-400">{displayTimestamp(message.timestamp)}</span>
                                  </div>
                                  <div
                                    className={`rounded-xl p-4 ${isBot ? 'bg-background border-primary/30 rounded-tl-md' : 'bg-primary/10 border-primary/30 rounded-tr-md'
                                      } border`}
                                  >
                                    <p className="text-sm">{message.message ?? message.text}</p>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-default-500">
                        <p>{t('No transcript data available.')}</p>
                      </div>
                    )}
                  </div>
                </Tab>

                <Tab key="logs" title={t('Logs')}>
                  <div className="rounded-xl border border-divider bg-default-50 p-6">
                    {isLoading && (
                      <div className="flex items-center gap-3 p-4">
                        <Spinner size="sm" />
                        <span>{t('Loading logs...')}</span>
                      </div>
                    )}
                    {logEntries.length > 0 ? (
                      <div>
                        <div className="flex items-center gap-4 mb-4">
                          <Input
                            size="sm"
                            placeholder={t('Search logs...')}
                            value={searchTerm}
                            onValueChange={setSearchTerm}
                            startContent={<Icon icon="mdi:magnify" className="w-4 h-4 text-default-400" />}
                            classNames={{ inputWrapper: 'max-w-[200px]' }}
                          />
                          <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="rounded-lg border border-divider bg-background px-3 py-2 text-sm"
                          >
                            <option value="all">{t('All Types')}</option>
                            <option value="PERF">{t('Performance')}</option>
                            <option value="ASSISTANT">{t('Assistant')}</option>
                            <option value="STT">{t('Speech-to-Text')}</option>
                            <option value="USAGE">{t('Usage')}</option>
                            <option value="EVENT">{t('Events')}</option>
                            <option value="LATENCY">{t('Duration')}</option>
                          </select>
                        </div>
                        <div className="overflow-y-auto max-h-[400px] space-y-3">
                          {filteredLogs.length === 0 ? (
                            <p className="text-center py-4 text-default-500">{t('No logs match the filter.')}</p>
                          ) : (
                            filteredLogs.map((entry, i) => (
                              <div
                                key={i}
                                className="rounded-lg border border-divider bg-background p-3 text-sm space-y-2"
                              >
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="font-semibold text-primary shrink-0">{t('Timestamp')}:</span>
                                  <span className="text-default-700">{entry.timestamp}</span>
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="font-semibold text-primary shrink-0">{t('Type')}:</span>
                                  <span
                                    className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${getLogTypeColor(entry.type)}`}
                                  >
                                    {entry.type}
                                  </span>
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="font-semibold text-primary shrink-0">{t('Service')}:</span>
                                  <span className="text-default-700">{entry.service}</span>
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="font-semibold text-primary shrink-0">{t('Metric')}:</span>
                                  <span className="text-default-700">{entry.metric}</span>
                                </div>
                                <div className="flex flex-col gap-1">
                                  <span className="font-semibold text-primary shrink-0">{t('Value')}:</span>
                                  <span className="text-default-700 break-words">{entry.value}</span>
                                </div>
                                {entry.runtime && (
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="font-semibold text-primary shrink-0">{t('Runtime')}:</span>
                                    <span className="text-default-700">{entry.runtime}</span>
                                  </div>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-default-500">
                        <p>{t('No session log available.')}</p>
                      </div>
                    )}
                  </div>
                </Tab>

                <Tab key="analysis" title={t('Analysis')}>
                  <div className="rounded-xl border border-divider bg-default-50 p-6">
                    {isLoading ? (
                      <div className="flex items-center gap-3 p-4">
                        <Spinner size="sm" />
                        <span>{t('Loading...')}</span>
                      </div>
                    ) : callDetail?.costs && callDetail.costs.length > 0 ? (
                      <div className="space-y-3">
                        {callDetail.costs
                          .filter((c) => c.type === 'analysis')
                          .map((cost, i) => (
                            <div key={i} className="rounded-lg border border-divider bg-background p-4">
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-primary">{t('Analysis')}</span>
                                <span className="text-sm font-medium text-success">${cost.cost.toFixed(6)}</span>
                              </div>
                              {(cost.prompt_tokens !== undefined || cost.completion_tokens !== undefined) && (
                                <p className="text-sm text-default-500 mt-2">
                                  {t('Tokens')}: {cost.prompt_tokens ?? 0} + {cost.completion_tokens ?? 0}
                                </p>
                              )}
                            </div>
                          ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-default-500">
                        <p>{t('No analysis data available.')}</p>
                      </div>
                    )}
                  </div>
                </Tab>

                <Tab key="cost" title={t('Call Cost')}>
                  <div className="rounded-xl border border-divider bg-default-50 p-6">
                    {isLoading && (
                      <div className="flex items-center gap-3 p-4">
                        <Spinner size="sm" />
                        <span>{t('Loading...')}</span>
                      </div>
                    )}
                    {callDetail?.costs && callDetail.costs.length > 0 ? (
                      <div className="space-y-4">
                        <div className="rounded-lg border border-divider bg-background p-4">
                          <div className="flex justify-between items-center">
                            <span className="font-semibold">{t('Total Cost')}</span>
                            <span className="text-success font-bold">
                              ${callDetail.costs.reduce((s, c) => s + c.cost, 0).toFixed(4)}
                            </span>
                          </div>
                        </div>
                        {callDetail.costs.map((cost, i) => (
                          <div key={i} className="rounded-lg border border-divider bg-background p-4">
                            <div className="flex justify-between">
                              <span className="text-sm font-medium">{cost.type}</span>
                              <span className="text-success font-medium">${cost.cost.toFixed(6)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-default-500">
                        <p>{t('No cost data available.')}</p>
                      </div>
                    )}
                  </div>
                </Tab>

                <Tab key="tools" title={t('Tools')}>
                  <div className="rounded-xl border border-divider bg-default-50 p-6">
                    {isLoading && (
                      <div className="flex items-center gap-3 p-4">
                        <Spinner size="sm" />
                        <span>{t('Loading transcript...')}</span>
                      </div>
                    )}
                    {!isLoading && toolTranscriptEntries.length > 0 ? (
                      <div className="flex max-h-[min(70vh,28rem)] flex-col gap-4 overflow-y-auto overflow-x-hidden pr-1 min-w-0">
                        {toolTranscriptEntries.map(({ message, index }) => (
                          <ToolTranscriptCard
                            key={`${message.timestamp ?? ''}-${message.type}-${index}`}
                            message={message}
                            index={index}
                            expanded={!!expandedMessages[index]}
                            onToggle={toggleMessageDetails}
                          />
                        ))}
                      </div>
                    ) : null}
                    {!isLoading && toolTranscriptEntries.length === 0 ? (
                      <div className="text-center py-8 text-default-500">
                        <p>{t('No tool calls for this call.')}</p>
                      </div>
                    ) : null}
                  </div>
                </Tab>

                <Tab key="summary" title={t('Summary')}>
                  <div className="rounded-xl border border-divider bg-default-50 p-6">
                    {isLoading && (
                      <div className="flex items-center gap-3 p-4">
                        <Spinner size="sm" />
                        <span>{t('Loading...')}</span>
                      </div>
                    )}
                    {callDetail?.summary ? (
                      <div className="space-y-4">
                        <div className="rounded-lg border border-divider bg-background p-4">
                          <p className="text-sm font-medium text-default-500 mb-2">{t('Summary')}</p>
                          <p className="text-sm whitespace-pre-wrap">{callDetail.summary.summary}</p>
                        </div>
                        <div className="rounded-lg border border-divider bg-background p-4">
                          <p className="text-sm font-medium text-default-500 mb-2">{t('Outcome')}</p>
                          <p className="text-sm">{callDetail.summary.outcome}</p>
                        </div>
                        <div className="rounded-lg border border-divider bg-background p-4">
                          <p className="text-sm font-medium text-default-500 mb-2">{t('Reasoning')}</p>
                          <p className="text-sm">{callDetail.summary.reasoning}</p>
                        </div>
                        {callDetail.summary.usage && (
                          <div className="rounded-lg border border-divider bg-background p-4">
                            <p className="text-sm font-medium text-default-500 mb-2">{t('Token Usage')}</p>
                            <p className="text-sm">
                              {t('Prompt')}: {callDetail.summary.usage.prompt_tokens ?? 'N/A'} |{' '}
                              {t('Completion')}: {callDetail.summary.usage.completion_tokens ?? 'N/A'}
                            </p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-default-500">
                        <p>{t('No summary available.')}</p>
                      </div>
                    )}
                  </div>
                </Tab>
              </Tabs>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CallLogDrawer;
